import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogTitle,
  EmptyState,
  InfoBox,
  Input,
  Label,
  LoadingState,
  Pagination,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Toast,
  kstDotDateStr,
} from '@biznomi/ui-react'
import {
  cancelPayslip,
  fileSizeText,
  getPayslipPeriods,
  getPayslips,
  openPayslipFile,
  payPeriodLabel,
  previewPayslips,
  recentPayPeriods,
  uploadPayslips,
  type AdminPayslip,
  type PayslipPreview,
  type PayslipStatus,
} from '@/lib/api/payslip'

/**
 * 급여명세서 관리 — **ADMIN 전용** (부서장도 못 본다. 서버도 동일).
 *
 * 우리가 하는 일은 전달·보관·증빙뿐이다 (§1). 급여를 계산하지도, 금액을 읽지도 않는다.
 *
 * 화면 설계의 핵심은 **오발송 방지**다. 남의 급여를 잘못 보내면 되돌릴 수 없으므로
 * 「파일 선택 → 누구에게 갈지 확인 → 보내기」 2단계로 나눴다. 1단계에서는
 * 파일을 서버로 올리지 않고 이름만 보낸다.
 */

const ALL = '__all__'
const PAGE_SIZE = 20

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: ALL, label: '전체' },
  { value: 'SENT', label: '미확인' },
  { value: 'VIEWED', label: '확인함' },
  { value: 'CANCELLED', label: '회수됨' },
]

function StatusBadge({ s }: { s: PayslipStatus }) {
  if (s === 'VIEWED') return <Badge variant="info">확인함</Badge>
  if (s === 'CANCELLED') return <Badge variant="outline">회수됨</Badge>
  return <Badge variant="warning">미확인</Badge>
}

/** 없으면 '—'. 있으면 KST 기준 날짜 — 기기 타임존을 따르면 해외·시계 오차에서 어긋난다 */
function dateText(iso: string | null): string {
  return iso ? kstDotDateStr(iso) : '—'
}

export default function AdminPayslips() {
  const qc = useQueryClient()
  const [page, setPage] = useState(1)
  const [period, setPeriod] = useState(ALL)
  const [status, setStatus] = useState(ALL)
  const [search, setSearch] = useState('')
  const [sendOpen, setSendOpen] = useState(false)
  const [cancelTarget, setCancelTarget] = useState<AdminPayslip | null>(null)
  const [opening, setOpening] = useState<string | null>(null)
  const [toast, setToast] = useState('')
  const [error, setError] = useState('')

  const { data: periodsData } = useQuery({
    queryKey: ['payslips', 'periods'],
    queryFn: getPayslipPeriods,
  })
  const periods = periodsData?.success ? periodsData.data ?? [] : []

  const { data, isLoading } = useQuery({
    queryKey: ['payslips', 'admin', page, period, status, search],
    queryFn: () =>
      getPayslips({
        page,
        limit: PAGE_SIZE,
        payPeriod: period === ALL ? undefined : period,
        status: status === ALL ? undefined : (status as PayslipStatus),
        search: search || undefined,
      }),
  })

  const result = data?.success ? data.data : undefined
  const rows = result?.items ?? []
  const total = result?.total ?? 0

  async function view(p: AdminPayslip) {
    setOpening(p.id)
    setError('')
    const res = await openPayslipFile(p.id)
    setOpening(null)
    if ('error' in res) {
      setError(res.error)
      return
    }
    const win = window.open(res.url, '_blank')
    if (!win) window.location.href = res.url
    setTimeout(() => URL.revokeObjectURL(res.url), 60_000)
  }

  async function doCancel() {
    if (!cancelTarget) return
    const res = await cancelPayslip(cancelTarget.id)
    setCancelTarget(null)
    if (!res.success) {
      setError(res.error ?? '회수에 실패했습니다')
      return
    }
    qc.invalidateQueries({ queryKey: ['payslips', 'admin'] })
    setToast('급여명세서를 회수했습니다')
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">급여명세서</h1>
        <Button size="sm" onClick={() => setSendOpen(true)}>명세서 보내기</Button>
      </div>

      <InfoBox tone="info">
        급여 프로그램에서 뽑은 PDF 를 사람별로 나눠 올려 주세요. 본인만 열 수 있고, 누가 언제
        열어 봤는지 남습니다. 파일 이름 앞에 사번을 붙이면 알아서 짝을 맞춥니다
        (예: <code>D001_홍길동.pdf</code>). 금액은 저희가 세지도, 보지도 않습니다.
      </InfoBox>

      <div className="flex flex-wrap gap-2">
        <Select value={period} onValueChange={(v) => { setPeriod(v); setPage(1) }}>
          <SelectTrigger className="w-40"><SelectValue placeholder="급여월" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>전체 급여월</SelectItem>
            {periods.map((p) => (
              <SelectItem key={p} value={p}>{payPeriodLabel(p)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1) }}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUS_FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          placeholder="직원 이름 검색"
          className="w-48"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1) }}
        />
      </div>

      {error && (
        <p role="alert" className="rounded-lg bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}

      {isLoading ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState title="급여명세서가 없습니다" hint="«명세서 보내기» 로 시작하세요." />
      ) : (
        <>
          <ul className="divide-y divide-slate-100 rounded-card bg-white">
            {rows.map((p) => (
              <li key={p.id} className="space-y-2 px-4 py-3.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium">
                      {p.employee.name}
                      <span className="ml-1.5 text-xs font-normal text-slate-400">
                        {[p.employee.employeeNumber, p.employee.department?.name]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </p>
                    <p className="mt-0.5 text-sm text-slate-600">{payPeriodLabel(p.payPeriod)}</p>
                    <p className="mt-0.5 truncate text-xs text-slate-400">
                      {p.fileName} · {fileSizeText(p.fileSize)} · 보낸 날 {dateText(p.createdAt)}
                      {p.viewedAt ? ` · 확인 ${dateText(p.viewedAt)}` : ''}
                    </p>
                  </div>
                  <StatusBadge s={p.status} />
                </div>

                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" loading={opening === p.id} onClick={() => void view(p)}>
                    보기
                  </Button>
                  {p.status !== 'CANCELLED' && (
                    <Button variant="secondary" size="sm" onClick={() => setCancelTarget(p)}>
                      회수
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <Pagination
            page={page}
            totalPages={Math.max(1, Math.ceil(total / PAGE_SIZE))}
            onChange={setPage}
          />
        </>
      )}

      {sendOpen && (
        <SendDialog
          onClose={() => setSendOpen(false)}
          onDone={(msg) => {
            setSendOpen(false)
            qc.invalidateQueries({ queryKey: ['payslips'] })
            setToast(msg)
          }}
        />
      )}

      {cancelTarget && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setCancelTarget(null)}
          title="급여명세서를 회수할까요?"
          message={`${cancelTarget.employee.name} 님 ${payPeriodLabel(cancelTarget.payPeriod)} 명세서를 회수합니다. 직원 화면에서 사라집니다.${
            cancelTarget.viewedAt ? ' 이미 열어 본 명세서라, 열어 봤다는 기록은 그대로 남습니다.' : ''
          }`}
          confirmText="회수"
          destructive
          onConfirm={() => void doCancel()}
        />
      )}

      {toast && <Toast message={toast} variant="success" onClose={() => setToast('')} />}
    </div>
  )
}

/**
 * 보내기 — 2단계.
 *   ① 파일 고르기 → 서버에 **이름만** 보내 누구에게 갈지 확인
 *   ② 확인 후 실제 업로드
 * 오발송이 되돌릴 수 없는 사고라서 확인 단계를 건너뛸 수 없게 했다.
 */
function SendDialog({
  onClose,
  onDone,
}: {
  onClose: () => void
  onDone: (message: string) => void
}) {
  const fileInput = useRef<HTMLInputElement>(null)
  const [payPeriod, setPayPeriod] = useState(() => recentPayPeriods(1)[0]!)
  const [files, setFiles] = useState<File[]>([])
  const [preview, setPreview] = useState<PayslipPreview | null>(null)
  const [replaceExisting, setReplaceExisting] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const periodOptions = recentPayPeriods(15)

  /** 파일이나 급여월이 바뀌면 확인 결과를 버린다 — 옛 확인으로 보내면 안 된다 */
  function reset() {
    setPreview(null)
    setReplaceExisting(false)
    setError('')
  }

  async function check() {
    if (files.length === 0) return setError('PDF 파일을 선택해 주세요')
    setBusy(true)
    setError('')
    const res = await previewPayslips(payPeriod, files.map((f) => f.name))
    setBusy(false)
    if (!res.success || !res.data) {
      setError(res.error ?? '확인에 실패했습니다')
      return
    }
    setPreview(res.data)
  }

  async function send() {
    if (!preview) return
    // 매칭된 파일만 올린다 (서버도 다시 검증한다)
    const okNames = new Set(preview.matches.filter((m) => m.employeeId).map((m) => m.fileName))
    const toSend = files.filter((f) => okNames.has(f.name))
    if (toSend.length === 0) return setError('보낼 수 있는 파일이 없습니다')

    setBusy(true)
    setError('')
    const res = await uploadPayslips({ payPeriod, files: toSend, replaceExisting })
    setBusy(false)
    if (!res.success || !res.data) {
      setError(res.error ?? '보내지 못했습니다')
      return
    }
    const d = res.data
    const parts = [`${payPeriodLabel(d.payPeriod)} ${d.createdCount}건 보냈습니다`]
    if (d.replacedCount > 0) parts.push(`기존 ${d.replacedCount}건 회수`)
    if (d.failed.length > 0) parts.push(`실패 ${d.failed.length}건`)
    onDone(parts.join(' · '))
  }

  const matched = preview?.matches.filter((m) => m.employeeId) ?? []
  const failed = preview?.matches.filter((m) => !m.employeeId) ?? []

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogTitle>급여명세서 보내기</DialogTitle>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>급여월</Label>
            <Select
              value={payPeriod}
              onValueChange={(v) => { setPayPeriod(v); reset() }}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {periodOptions.map((p) => (
                  <SelectItem key={p} value={p}>{payPeriodLabel(p)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="payslip-files">파일 (PDF 여러 개, 1건당 최대 5MB)</Label>
            <input
              id="payslip-files"
              ref={fileInput}
              type="file"
              accept="application/pdf,.pdf"
              multiple
              className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium"
              onChange={(e) => { setFiles(Array.from(e.target.files ?? [])); reset() }}
            />
            <p className="text-xs text-slate-400">
              파일 이름 앞에 사번을 붙여 주세요. 예: <code>D001_홍길동.pdf</code>
            </p>
            {files.length > 0 && !preview && (
              <p className="text-xs text-slate-500">{files.length}개 선택됨</p>
            )}
          </div>

          {/* ① 확인 단계 */}
          {!preview && (
            <Button className="w-full" loading={busy} disabled={files.length === 0} onClick={() => void check()}>
              누구에게 갈지 확인
            </Button>
          )}

          {/* ② 확인 결과 */}
          {preview && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="info">보낼 수 있음 {matched.length}</Badge>
                {failed.length > 0 && <Badge variant="destructive">매칭 실패 {failed.length}</Badge>}
              </div>

              <ul className="max-h-52 space-y-1 overflow-y-auto rounded-lg bg-slate-50 p-2 text-xs">
                {preview.matches.map((m) => (
                  <li key={m.fileName} className="flex items-start gap-2">
                    <span className={m.employeeId ? 'text-primary' : 'text-[var(--color-danger)]'}>
                      {m.employeeId ? '✓' : '✕'}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-slate-500">{m.fileName}</span>
                      <span className={m.employeeId ? 'font-medium' : 'text-[var(--color-danger)]'}>
                        {m.employeeId
                          ? `→ ${m.employeeName}${m.employeeNumber ? ` (${m.employeeNumber})` : ''}`
                          : m.reason}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>

              {failed.length > 0 && (
                <p className="text-xs text-slate-500">
                  짝을 못 찾은 {failed.length}건은 안 보냅니다. 파일 이름을 고쳐서 다시 올려 주세요.
                </p>
              )}

              {preview.existing.length > 0 && (
                <div className="space-y-2 rounded-lg bg-[var(--color-danger-bg)] p-3">
                  <p className="text-sm font-medium text-[var(--color-danger)]">
                    이 달에 이미 보낸 명세서가 {preview.existing.length}건 있습니다
                  </p>
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-0.5 size-4 shrink-0"
                      checked={replaceExisting}
                      onChange={(e) => setReplaceExisting(e.target.checked)}
                    />
                    <span>기존 건을 회수하고 새로 보냅니다 (정정 재발행)</span>
                  </label>
                  {!replaceExisting && (
                    <p className="text-xs text-slate-500">
                      체크 안 하면 같은 달에 명세서가 두 장씩 쌓입니다.
                    </p>
                  )}
                </div>
              )}

              {error && <p role="alert" className="text-sm text-[var(--color-danger)]">{error}</p>}

              <div className="flex gap-2">
                <Button variant="secondary" className="flex-1" onClick={reset} disabled={busy}>
                  다시 고르기
                </Button>
                <Button
                  className="flex-1"
                  loading={busy}
                  disabled={matched.length === 0}
                  onClick={() => void send()}
                >
                  {matched.length}건 보내기
                </Button>
              </div>
            </div>
          )}

          {!preview && error && (
            <p role="alert" className="text-sm text-[var(--color-danger)]">{error}</p>
          )}

          {!preview && (
            <Button variant="secondary" className="w-full" onClick={onClose} disabled={busy}>
              취소
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
